import { useCallback, useEffect, useState } from 'react';
import API from '../api/API';

/**
 * Chi sono e cosa posso fare, da /api/me e /api/me/ambiti.
 *
 * I permessi non stanno nel token: il backend li risolve a ogni richiesta, e questo hook li tiene
 * per la durata della pagina in una cache di modulo, cosi' sidebar e pagina non ripetono la stessa
 * chiamata. Serve a decidere cosa mostrare e cosa disabilitare: il controllo che conta resta
 * quello del server, che risponde 403 comunque.
 */
let cache = null;
let inCorso = null;

const carica = async () => {
  const [profilo, ambiti, daDecidere] = await Promise.all([
    API.me.profilo(),
    API.me.ambiti(),
    API.approvazioni.count()
  ]);
  return {
    me: profilo.isSuccess ? profilo.data : null,
    ambiti: ambiti.isSuccess ? ambiti.data : null,
    daDecidere: daDecidere.isSuccess ? (daDecidere.data ?? 0) : 0
  };
};

/** Da chiamare dopo aver scritto la matrice: il prossimo lettore ricarica. */
export const invalidaMe = () => {
  cache = null;
  inCorso = null;
};

export const useMe = () => {
  const [stato, setStato] = useState(cache);
  const [loading, setLoading] = useState(cache === null);

  const leggi = useCallback(async (forza = false) => {
    if (forza) invalidaMe();
    if (cache) {
      setStato(cache);
      setLoading(false);
      return cache;
    }

    setLoading(true);
    try {
      inCorso = inCorso ?? carica();
      const dati = await inCorso;
      cache = dati;
      setStato(dati);
      return dati;
    }
    catch (errore) {
      // Una lettura che va male non deve lasciare la pagina in caricamento per sempre: si esce con
      // uno stato vuoto, e chi usa l'hook mostra quello che puo' mostrare senza permessi.
      console.error('Lettura di /api/me non riuscita', errore);
      const vuoto = { me: null, ambiti: null, daDecidere: 0 };
      cache = vuoto;
      setStato(vuoto);
      return vuoto;
    }
    finally {
      inCorso = null;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cache) {
      leggi();
    }
  }, [leggi]);

  return {
    me: stato?.me ?? null,
    ambiti: stato?.ambiti ?? null,
    daDecidere: stato?.daDecidere ?? 0,
    loading,
    ricarica: () => leggi(true)
  };
};

/**
 * Il livello piu' alto che l'utente corrente puo' concedere su una risorsa in un ambito: e' quello
 * che ha lui. Con il bypass non c'e' limite. Serve a disabilitare le opzioni che il server
 * rifiuterebbe, invece di far scoprire il limite con un errore.
 */
export const livelloMassimo = (me, ambito, risorsa) => {
  if (!me) return 'NEGATO';
  if (me.bypass) return 'SCRITTURA';

  if (ambito.tipo === 'GLB') return me.globale?.[risorsa] ?? 'NEGATO';

  const lista = ambito.tipo === 'GT' ? me.gruppiTornei : me.edizioni;
  const trovato = (lista ?? []).find(a => a.id === ambito.id);

  // /api/me porta le matrici gia' risolte, e un ambito compare solo se qualcosa vi arriva:
  // se non c'e', li' non ha niente.
  return trovato ? (trovato.permessi?.[risorsa] ?? 'NEGATO') : 'NEGATO';
};

export default useMe;
